/**
 * Customer Portal (UI) - account context state and data loading.
 *
 * This hook separates viewer identity from selected scoped account so partner
 * and reseller flows can switch accounts without mutating user identity.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAccountContext, fetchSession, setActiveOrganization as setActiveOrganizationRequest } from '../api/client';
import type { AccessibleAccount, ViewerSession } from '../../types/portal';

export type AccountContextState = {
  loading: boolean;
  error: string | null;
  session: ViewerSession | null;
  accounts: AccessibleAccount[];
  selectedAccountId: string | null;
  canSwitchAccounts: boolean;
  setSelectedAccountId: (next: string) => void;
  switchError: string | null;
  reload: () => Promise<void>;
};

export function useAccountContextStore(): AccountContextState {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<ViewerSession | null>(null);
  const [accounts, setAccounts] = useState<AccessibleAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const reload = useCallback(
    async (options?: { cancelled?: () => boolean }) => {
      setLoading(true);
      setError(null);
      setSwitchError(null);
      try {
        const sessionPayload = await fetchSession();
        if (options?.cancelled?.()) return;
        if (!sessionPayload.session) {
          setSession(null);
          setAccounts([]);
          setSelectedAccountId(null);
          return;
        }
        setSession(sessionPayload.session);
        const contextPayload = await fetchAccountContext();
        if (options?.cancelled?.()) return;
        setAccounts(contextPayload.accounts ?? []);
        setSelectedAccountId(contextPayload.defaultAccountId ?? null);
      } catch (err) {
        if (options?.cancelled?.()) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (options?.cancelled?.()) return;
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    void reload({ cancelled: () => cancelled });

    return () => {
      cancelled = true;
    };
  }, [reload]);

  const canSwitchAccounts = useMemo(() => {
    if (!session) return false;
    if (session.accountType === 'referral_partner') {
      return accounts.length > 1;
    }
    const distinctOrganizations = new Set(accounts.map((account) => (account.organizationId ?? '').trim()).filter((value) => value.length > 0));
    return distinctOrganizations.size > 1;
  }, [accounts, session]);

  const selectAccount = useCallback(
    async (next: string) => {
      setSwitchError(null);
      const target = accounts.find((account) => account.id === next) ?? null;
      if (!target) return;
      const targetOrganizationId = typeof target.organizationId === 'string' ? target.organizationId.trim() : '';
      const currentOrganizationId = typeof session?.organizationId === 'string' ? session.organizationId.trim() : '';
      if (targetOrganizationId && targetOrganizationId !== currentOrganizationId) {
        setLoading(true);
        try {
          await setActiveOrganizationRequest(targetOrganizationId);
          await reload();
        } catch (error) {
          setSwitchError(error instanceof Error ? error.message : String(error));
          setLoading(false);
        }
        return;
      }
      setSelectedAccountId(next);
    },
    [accounts, reload, session?.organizationId]
  );

  return {
    loading,
    error,
    session,
    accounts,
    selectedAccountId,
    canSwitchAccounts,
    setSelectedAccountId: (next: string) => {
      void selectAccount(next);
    },
    switchError,
    reload: async () => {
      await reload();
    }
  };
}
